import type { OrderStatus, StoreInfo } from '@multicrm/shared';
import { orderStatusLabel } from '@multicrm/shared';
import { motion } from 'motion/react';
import { cn } from '../lib/cn';
import { useI18n } from '../lib/i18n';
import { availableStatuses, type StatusTab } from '../lib/statuses';
import { haptics } from '../lib/telegram';

/** Вкладки по статусам магазина, с названиями как на сайте/в CRM и счётчиками. */
export function StatusTabs({
  value,
  onChange,
  stores,
  counts,
  total,
}: {
  value: StatusTab;
  onChange: (tab: StatusTab) => void;
  stores: StoreInfo[];
  counts?: Partial<Record<OrderStatus, number>>;
  total?: number;
}) {
  const { t, locale } = useI18n();
  const statuses = availableStatuses(stores);

  const label = (status: OrderStatus): string => {
    const custom = stores.find((store) => store.statusLabels?.[status])?.statusLabels?.[status];
    return custom ?? orderStatusLabel(status, locale);
  };

  const tabs: Array<{ tab: StatusTab; text: string; count: number | undefined }> = [
    { tab: 'all', text: t('app.all'), count: total },
    ...statuses.map((status) => ({ tab: status, text: label(status), count: counts?.[status] })),
  ];

  return (
    <div className="no-scrollbar flex gap-1.5 overflow-x-auto px-4 pb-2.5">
      {tabs.map(({ tab, text, count }) => {
        const isActive = tab === value;

        return (
          <button
            key={tab}
            type="button"
            onClick={() => {
              if (isActive) return;
              haptics.select();
              onChange(tab);
            }}
            className={cn(
              'rounded-chip relative shrink-0 px-3.5 py-1.5 text-[14px] whitespace-nowrap transition-colors duration-150',
              isActive ? 'text-accent-text' : 'text-muted bg-surface-2',
            )}
          >
            {isActive && (
              <motion.span
                layoutId="status-tab"
                className="bg-accent rounded-chip absolute inset-0"
                transition={{ type: 'spring', stiffness: 480, damping: 36 }}
              />
            )}
            <span className="relative">
              {text}
              {count !== undefined && (
                <span className={cn('ml-1.5 text-[12px]', isActive ? 'opacity-80' : 'opacity-70')}>
                  {count}
                </span>
              )}
            </span>
          </button>
        );
      })}
    </div>
  );
}

export { availableStatuses, statusesForTab, type StatusTab } from '../lib/statuses';
